/**
 * Custom Selectors — multi-step product configurator
 * Manages brand/color card selection, file upload per placement,
 * Order Summary, and sliding step transitions.
 */
(function () {
  'use strict';

  class CustomSelectors {
    constructor(el) {
      this.el             = el;
      this.formId         = el.dataset.formId;
      this.form           = document.getElementById(this.formId);
      this.sectionEl      = document.getElementById('shopify-section-' + el.dataset.sectionId);
      this.colorSlot      = el.querySelector('[data-color-slot]');
      this.steps          = Array.from(el.querySelectorAll('.custom-selectors__step'));
      this.uploadEndpoint = el.dataset.uploadEndpoint || '';
      this.productId      = el.dataset.productId      || '';
      this._designToken   = null; // generated on first upload, reused across placements
      this.currentIdx     = 0;

      // Find variant-selects and remember its original DOM position (for Back nav)
      this.variantPicker        = this.sectionEl?.querySelector('variant-selects');
      this.pickerOriginalParent = this.variantPicker?.parentElement || null;
      this.pickerOriginalNext   = this.variantPicker?.nextSibling   || null;

      // Start: brand step visible, everything else hidden
      this.sectionEl?.classList.add('cs-brand-step');

      this._initSteps();

      // Enable color Continue immediately if a variant is pre-selected
      this.colorSlot?.addEventListener('change', (e) => {
        if (e.target.type === 'radio') this._enableColorContinue();
      });

      this.el.addEventListener('click', this._onClick.bind(this));
    }

    /* ── Setup ──────────────────────────────────────────────────────────────── */

    _initSteps() {
      this.steps.forEach((step, i) => {
        step.style.transition = 'none';
        step.style.transform  = `translateX(${i * 100}%)`;
      });

      // Double-rAF ensures accurate height after single-column grid reflow on mobile
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.steps.forEach((step) => { step.style.transition = ''; });
          this._setHeight(this.steps[0]);
        });
      });

      if (typeof ResizeObserver !== 'undefined') {
        this._ro = new ResizeObserver(() => {
          if (!this._transitioning) this._setHeight(this.steps[this.currentIdx]);
        });
        this._ro.observe(this.steps[0]);
      }
    }

    /* ── Event routing ──────────────────────────────────────────────────────── */

    _onClick(e) {
      const card = e.target.closest('.brand-card');
      if (card) { this._selectCard(card); return; }

      const continueBtn = e.target.closest('[data-continue-step]');
      if (continueBtn && !continueBtn.disabled) {
        this._onContinue(continueBtn.dataset.continueStep);
        return;
      }

      const backBtn = e.target.closest('[data-back-step]');
      if (backBtn) { this._onBack(backBtn.dataset.backStep); return; }

      // Upload zone: "Change file" or "Try again" — re-trigger the file input
      const changeBtn = e.target.closest('.upload-zone__change-btn, .upload-zone__retry-btn');
      if (changeBtn) {
        const zone = changeBtn.closest('.upload-zone');
        zone?.querySelector('.upload-zone__input')?.click();
        return;
      }

      // "Open Editor" button — launch the Konva mockup editor
      const editorBtn = e.target.closest('.upload-zone__editor-btn');
      if (editorBtn) {
        const zone = editorBtn.closest('.upload-zone');
        const card = zone?.closest('.placement-card');
        if (zone && card) this._openArtworkEditor(zone, card);
        return;
      }

      // Clicking the drop area itself (not a button inside it)
      const droparea = e.target.closest('.upload-zone__droparea');
      if (droparea && !e.target.closest('button')) {
        const zone = droparea.closest('.upload-zone');
        zone?.querySelector('.upload-zone__input')?.click();
        return;
      }
    }

    /* ── Brand card selection ───────────────────────────────────────────────── */

    _selectCard(card) {
      const group = card.closest('[role="radiogroup"]');
      if (!group) return;

      group.querySelectorAll('.brand-card--selected').forEach((c) => {
        c.classList.remove('brand-card--selected');
        c.setAttribute('aria-pressed', 'false');
      });

      card.classList.add('brand-card--selected');
      card.setAttribute('aria-pressed', 'true');

      const key   = card.dataset.propertyKey;
      const value = card.dataset.propertyValue;
      if (key) this._setProperty(key, value);

      const step = card.closest('.custom-selectors__step');
      const btn  = step?.querySelector('.custom-selectors__continue');
      if (btn) btn.disabled = false;
    }

    /* ── Line item properties ───────────────────────────────────────────────── */

    _setProperty(key, value) {
      if (!this.form) return;
      const name  = `properties[${key}]`;
      let   input = this.form.querySelector(`[name="${CSS.escape(name)}"]`)
                 || this.form.querySelector(`[name='${name}']`);
      if (!input) {
        input       = document.createElement('input');
        input.type  = 'hidden';
        input.name  = name;
        this.form.appendChild(input);
      }
      input.value = value;
    }

    /* ── Step navigation ────────────────────────────────────────────────────── */

    _onContinue(step) {
      if (step === 'brand') {
        if (this.variantPicker && this.colorSlot) {
          this.colorSlot.appendChild(this.variantPicker);
        }
        const hasChecked = this.colorSlot?.querySelector('input[type="radio"]:checked');
        if (hasChecked) this._enableColorContinue();

        this._goToStep(1);
        this.sectionEl?.classList.replace('cs-brand-step', 'cs-color-step');
      }

      if (step === 'color') {
        this._goToStep(2);
        this.sectionEl?.classList.replace('cs-color-step', 'cs-placement-step');
        this._initUploadZones();
      }

      if (step === 'placement') {
        this._initSizingStep();
        this._goToStep(3);
        this.sectionEl?.classList.replace('cs-placement-step', 'cs-sizing-step');
      }

      if (step === 'sizing') {
        this._updateSizeProperties();
        this._populateSummary();
        this._goToStep(4);
        this.sectionEl?.classList.replace('cs-sizing-step', 'cs-summary-step');
        this.sectionEl?.classList.add('cs-has-sizing');
        this._interceptProductForm();
      }

      this.el.dispatchEvent(new CustomEvent('custom-selectors:continue', {
        bubbles: true,
        detail:  { step },
      }));
    }

    _onBack(step) {
      if (step === 'color') {
        if (this.variantPicker && this.pickerOriginalParent) {
          if (this.pickerOriginalNext) {
            this.pickerOriginalParent.insertBefore(this.variantPicker, this.pickerOriginalNext);
          } else {
            this.pickerOriginalParent.appendChild(this.variantPicker);
          }
        }
        this._goToStep(0);
        this.sectionEl?.classList.replace('cs-color-step', 'cs-brand-step');
      }

      if (step === 'placement') {
        this._goToStep(1);
        this.sectionEl?.classList.replace('cs-placement-step', 'cs-color-step');
      }

      if (step === 'sizing') {
        this._goToStep(2);
        this.sectionEl?.classList.replace('cs-sizing-step', 'cs-placement-step');
      }

      if (step === 'summary') {
        this._goToStep(3);
        this.sectionEl?.classList.replace('cs-summary-step', 'cs-sizing-step');
        this.sectionEl?.classList.remove('cs-has-sizing');
      }
    }

    /* ── Slide animation ────────────────────────────────────────────────────── */

    _goToStep(index) {
      const fromH = this.steps[this.currentIdx]?.offsetHeight || 0;
      const toH   = this.steps[index]?.offsetHeight           || 0;

      this._transitioning = true;
      this.el.style.height = Math.max(fromH, toH) + 'px';

      this.steps.forEach((step, i) => {
        step.style.transform = `translateX(${(i - index) * 100}%)`;
      });

      setTimeout(() => {
        this._transitioning = false;
        this._setHeight(this.steps[index]);
        if (this._ro) {
          this._ro.disconnect();
          this._ro.observe(this.steps[index]);
        }
      }, 400);

      this.currentIdx = index;
    }

    _setHeight(step) {
      if (step) this.el.style.height = step.offsetHeight + 'px';
    }

    /* ── Helpers ────────────────────────────────────────────────────────────── */

    _enableColorContinue() {
      const btn = this.el.querySelector('[data-step="color"] .custom-selectors__continue');
      if (btn) btn.disabled = false;
    }

    /* ── Upload zones ───────────────────────────────────────────────────────── */

    /**
     * Wire up drag-drop + file-input handlers for every upload zone.
     * Called once when the user enters the placement step for the first time;
     * guard flag prevents double-binding on Back → Continue.
     */
    _initUploadZones() {
      if (this._uploadZonesReady) return;
      this._uploadZonesReady = true;

      const placementStep = this.el.querySelector('[data-step="placement"]');
      if (!placementStep) return;

      placementStep.querySelectorAll('.upload-zone').forEach((zone) => {
        const input    = zone.querySelector('.upload-zone__input');
        const droparea = zone.querySelector('.upload-zone__droparea');
        const card     = zone.closest('.placement-card');

        // Keyboard accessibility on the droparea
        droparea.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); }
        });

        // Drag-over highlight
        droparea.addEventListener('dragover', (e) => {
          e.preventDefault();
          droparea.classList.add('upload-zone__droparea--dragover');
        });
        ['dragleave', 'dragend'].forEach((evt) => {
          droparea.addEventListener(evt, () => {
            droparea.classList.remove('upload-zone__droparea--dragover');
          });
        });

        // Drop
        droparea.addEventListener('drop', (e) => {
          e.preventDefault();
          droparea.classList.remove('upload-zone__droparea--dragover');
          const file = e.dataTransfer.files[0];
          if (file) this._handleFile(zone, card, file);
        });

        // File input change (also used by "Change file" / "Try again" buttons)
        input.addEventListener('change', () => {
          const file = input.files[0];
          if (file) this._handleFile(zone, card, file);
          // Reset so the same file can be re-selected
          input.value = '';
        });
      });
    }

    /**
     * Handle a file chosen by the user for a given zone.
     *
     * Files are NOT uploaded to the CDN here. Instead the file is read as a
     * data URL and stored in IndexedDB under a per-session token. The token is
     * written to the product form so it travels with the cart line item.
     *
     * pcd-checkout-guard.js (loaded sitewide via theme.liquid) intercepts the
     * Shopify checkout button, reads the designs from IDB, uploads each one,
     * and swaps the token / placeholder properties for real CDN URLs before
     * redirecting to /checkout.
     */
    async _handleFile(zone, card, file) {
      // Validate file type
      const allowed = ['image/png', 'image/svg+xml', 'application/pdf',
                       'application/postscript', 'application/illustrator',
                       'image/x-eps', 'image/eps', 'application/eps'];
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowed.includes(file.type) && !['png','svg','pdf','ai','eps'].includes(ext)) {
        this._setZoneState(zone, 'error', 'Unsupported file type. Please use PNG, SVG, PDF, or AI.');
        return;
      }

      // Show spinner while FileReader runs (large files can take a moment)
      this._setZoneState(zone, 'uploading');

      try {
        // ── 1. Read file as data URL ───────────────────────────────────────────
        const dataUrl = await this._fileToDataUrl(file);

        // ── 2. Ensure a session token exists ──────────────────────────────────
        if (!this._designToken) {
          this._designToken = 'cs_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
        }

        const placement     = zone.dataset.uploadZone || 'front'; // 'front' | 'back' | 'left' | 'right'
        const placementName = card.dataset.placementName || placement;

        // ── 3. Persist in IndexedDB (same DB/store as product-customizer.js) ──
        await this._idbSavePlacement(this._designToken, placement, {
          placementName,
          previewUrl: dataUrl,   // pcd-checkout-guard reads this field
          productId:  this.productId,
          fileName:   file.name,
        });

        // ── 4. Register endpoint in localStorage for pcd-checkout-guard.js ────
        if (this.uploadEndpoint) {
          localStorage.setItem('pcd_upload_endpoint', this.uploadEndpoint);
        }

        // ── 5. Write form properties ───────────────────────────────────────────
        //   _design_token  → guard reads this to find the IDB entry
        //   Design {name}  → human-readable placeholder shown in Shopify cart;
        //                     the guard replaces this with the CDN URL at checkout
        this._setProperty('_design_token', this._designToken);
        this._setProperty(`Design ${placementName}`, file.name);

        // ── 6. Show local preview (blob URL — no network request) ─────────────
        const doneState  = zone.querySelector('.upload-zone__state--done');
        const previewImg = doneState?.querySelector('.upload-zone__preview');
        const fileIcon   = doneState?.querySelector('.upload-zone__file-icon');
        const nameEl     = doneState?.querySelector('.upload-zone__filename');

        if (nameEl) nameEl.textContent = file.name;

        const isImage = file.type.startsWith('image/');
        if (previewImg && fileIcon) {
          if (isImage) {
            previewImg.src    = URL.createObjectURL(file);
            previewImg.hidden = false;
            fileIcon.hidden   = true;
          } else {
            previewImg.hidden = true;
            fileIcon.hidden   = false;
          }
        }

        // Show / hide "Open Editor" button — only available for image files
        const editorBtn  = doneState?.querySelector('.upload-zone__editor-btn');
        if (editorBtn) editorBtn.hidden = !isImage;

        card.classList.add('placement-card--done');
        this._setZoneState(zone, 'done');
        this._checkUploadContinue();

      } catch (err) {
        console.error('[custom-selectors] File save failed:', err);
        const msg = err.message?.length < 120 ? err.message : 'Failed to save design. Please try again.';
        this._setZoneState(zone, 'error', msg);
        card.classList.remove('placement-card--done');
        this._checkUploadContinue();
      }
    }

    /* ── File → data URL ────────────────────────────────────────────────────── */

    _fileToDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = (e) => resolve(e.target.result);
        reader.onerror = ()  => reject(new Error('Could not read file'));
        reader.readAsDataURL(file);
      });
    }

    /* ── IndexedDB helpers (mirror pcd-checkout-guard.js) ───────────────────── */

    _idbOpen() {
      return new Promise((resolve, reject) => {
        const req = indexedDB.open('pcd-designs', 1);
        req.onupgradeneeded = (e) => e.target.result.createObjectStore('designs');
        req.onsuccess       = (e) => resolve(e.target.result);
        req.onerror         = (e) => reject(e.target.error);
      });
    }

    async _idbGet(key) {
      const db = await this._idbOpen();
      return new Promise((resolve, reject) => {
        const req = db.transaction('designs', 'readonly').objectStore('designs').get(key);
        req.onsuccess = (e) => resolve(e.target.result ?? null);
        req.onerror   = (e) => reject(e.target.error);
      });
    }

    async _idbPut(key, value) {
      const db = await this._idbOpen();
      return new Promise((resolve, reject) => {
        const tx = db.transaction('designs', 'readwrite');
        tx.objectStore('designs').put(value, key);
        tx.oncomplete = resolve;
        tx.onerror    = (e) => reject(e.target.error);
      });
    }

    /**
     * Read the existing IDB entry for this token (if any), merge in the new
     * placement, then write back. Handles the case where the user uploads to
     * multiple placements or re-uploads to a placement they already saved.
     */
    async _idbSavePlacement(token, placement, data) {
      const key      = 'pcd_designs_' + token;
      const existing = (await this._idbGet(key)) || {};
      existing[placement] = data;
      await this._idbPut(key, existing);
    }

    /** Switch which state div is visible inside the upload zone */
    _setZoneState(zone, state, errorMsg) {
      const states = ['idle', 'uploading', 'done', 'error'];
      states.forEach((s) => {
        const el = zone.querySelector(`.upload-zone__state--${s}`);
        if (el) el.hidden = (s !== state);
      });

      if (state === 'error' && errorMsg) {
        const msgEl = zone.querySelector('.upload-zone__error-msg');
        if (msgEl) msgEl.textContent = errorMsg;
      }
    }

    /** Enable the placement Continue button when at least one file is uploaded */
    _checkUploadContinue() {
      const placementStep = this.el.querySelector('[data-step="placement"]');
      const hasDone       = !!placementStep?.querySelector('.placement-card--done');
      const continueBtn   = placementStep?.querySelector('.custom-selectors__continue');
      if (continueBtn) continueBtn.disabled = !hasDone;
    }

    /* ── Order Summary population ───────────────────────────────────────────── */

    _populateSummary() {
      const summaryStep = this.el.querySelector('[data-step="summary"]');
      if (!summaryStep) return;

      // Garment — from brand selection hidden input
      const garmentVal = this.form
        ?.querySelector('[name="properties[Brand]"]')?.value || '—';
      const garmentEl = summaryStep.querySelector('[data-summary-garment]');
      if (garmentEl) garmentEl.textContent = garmentVal;

      // Color — from checked variant radio's label title
      const checkedRadio = this.colorSlot?.querySelector('input[type="radio"]:checked');
      let colorName = '—';
      if (checkedRadio) {
        const label = this.colorSlot.querySelector(
          `label[for="${CSS.escape(checkedRadio.id)}"]`
        );
        colorName = label?.getAttribute('title') || label?.textContent?.trim() || '—';
      }
      const colorEl = summaryStep.querySelector('[data-summary-color]');
      if (colorEl) colorEl.textContent = colorName;

      // Artwork Placements — from cards that completed upload
      const placementStep = this.el.querySelector('[data-step="placement"]');
      const doneCards = Array.from(
        placementStep?.querySelectorAll('.placement-card--done') || []
      );
      const names = doneCards
        .map((c) => c.dataset.placementName || '')
        .filter(Boolean);

      const placementsEl = summaryStep.querySelector('[data-summary-placements]');
      if (placementsEl) placementsEl.textContent = names.join(', ') || '—';

      // Design thumbnails — image previews from each done zone
      const thumbsContainer = summaryStep.querySelector('[data-summary-thumbs]');
      if (thumbsContainer) {
        thumbsContainer.innerHTML = '';
        doneCards.forEach((card) => {
          const previewImg = card.querySelector('.upload-zone__preview');
          if (previewImg && !previewImg.hidden && previewImg.src) {
            const img     = document.createElement('img');
            img.src       = previewImg.src;
            img.alt       = card.dataset.placementName || '';
            img.className = 'order-summary__thumb';
            thumbsContainer.appendChild(img);
          }
        });
      }

      // Sizes — from sizing breakdown
      const sizesEl    = summaryStep.querySelector('[data-summary-sizes]');
      const totalPcsEl = summaryStep.querySelector('[data-summary-total-pcs]');
      const sizeQtys   = this._sizeQuantities || {};
      const sizeParts  = Object.entries(sizeQtys)
        .filter(([, qty]) => qty > 0)
        .map(([size, qty]) => `${size} × ${qty}`);
      const totalPcs = Object.values(sizeQtys).reduce((s, q) => s + q, 0);

      if (sizesEl) sizesEl.textContent = sizeParts.length ? sizeParts.join(', ') : '—';
      if (totalPcsEl) {
        totalPcsEl.textContent = totalPcs > 0 ? `${totalPcs} pieces total` : '';
      }
    }

    /* ── Sizing Breakdown ───────────────────────────────────────────────────── */

    /**
     * Wire up stepper buttons and direct input editing.
     * Guard prevents double-binding on Back → Continue.
     */
    _initSizingStep() {
      if (this._sizingReady) return;
      this._sizingReady = true;

      this._sizeQuantities = {};

      const sizingStep = this.el.querySelector('[data-step="sizing"]');
      if (!sizingStep) return;

      this._sizingStep  = sizingStep;
      this._minOrder    = parseInt(sizingStep.dataset.minOrder || '12', 10);

      sizingStep.querySelectorAll('.sizing-table__row').forEach((row) => {
        const size  = row.dataset.size;
        this._sizeQuantities[size] = 0;

        const decBtn = row.querySelector('[data-stepper="dec"]');
        const incBtn = row.querySelector('[data-stepper="inc"]');
        const input  = row.querySelector('.sizing-stepper__input');

        decBtn?.addEventListener('click', () => {
          const cur = parseInt(row.dataset.qty || '0', 10);
          this._setRowQty(row, size, input, Math.max(0, cur - 1));
        });

        incBtn?.addEventListener('click', () => {
          const cur = parseInt(row.dataset.qty || '0', 10);
          this._setRowQty(row, size, input, cur + 1);
        });

        input?.addEventListener('change', () => {
          const val = Math.max(0, parseInt(input.value || '0', 10) || 0);
          this._setRowQty(row, size, input, val);
        });

        input?.addEventListener('blur', () => {
          const val = Math.max(0, parseInt(input.value || '0', 10) || 0);
          this._setRowQty(row, size, input, val);
        });
      });

      this._updateSizingTotal();
    }

    _setRowQty(row, size, input, qty) {
      row.dataset.qty = qty;
      if (input) input.value = qty;

      this._sizeQuantities[size] = qty;

      const subtotalEl = row.querySelector('.sizing-table__subtotal');
      if (subtotalEl) {
        subtotalEl.textContent = qty > 0 ? `${qty} pc${qty !== 1 ? 's' : ''}` : '—';
      }

      this._updateSizingTotal();
    }

    _updateSizingTotal() {
      const sizingStep = this._sizingStep || this.el.querySelector('[data-step="sizing"]');
      if (!sizingStep) return;

      const total    = Object.values(this._sizeQuantities || {}).reduce((s, q) => s + q, 0);
      const minOrder = this._minOrder || 12;

      // Update total row
      const totalQtyEl = sizingStep.querySelector('[data-sizing-total-qty]');
      const totalPcsEl = sizingStep.querySelector('[data-sizing-total-pcs]');
      if (totalQtyEl) totalQtyEl.textContent = total;
      if (totalPcsEl) totalPcsEl.textContent = `${total} piece${total !== 1 ? 's' : ''}`;

      // Update messages
      const warnEl    = sizingStep.querySelector('[data-sizing-message="warning"]');
      const successEl = sizingStep.querySelector('[data-sizing-message="success"]');
      const warnText  = warnEl?.querySelector('[data-sizing-message-text]');
      const succText  = successEl?.querySelector('[data-sizing-message-text]');

      if (total === 0) {
        if (warnEl)    warnEl.hidden    = true;
        if (successEl) successEl.hidden = true;
      } else if (total < minOrder) {
        const needed = minOrder - total;
        if (warnText)  warnText.textContent = `Minimum order is ${minOrder} pieces. You have ${total} — add ${needed} more to continue.`;
        if (warnEl)    warnEl.hidden    = false;
        if (successEl) successEl.hidden = true;
      } else {
        if (succText)  succText.textContent = `${total} pieces — meets minimum order requirement.`;
        if (successEl) successEl.hidden = false;
        if (warnEl)    warnEl.hidden    = true;
      }

      // Highlight active volume pricing tier
      sizingStep.querySelectorAll('.volume-pricing__tier').forEach((tier, i) => {
        const ranges = [[12,23],[24,47],[48,99],[100,Infinity]];
        const [lo, hi] = ranges[i] || [0, 0];
        tier.classList.toggle('volume-pricing__tier--active', total >= lo && total <= hi);
      });

      // Gate the Continue button
      const continueBtn = sizingStep.querySelector('[data-continue-step="sizing"]');
      if (continueBtn) continueBtn.disabled = (total < minOrder);
    }

    /** Write each non-zero size as a form property: "Size XS: 5" */
    _updateSizeProperties() {
      const qtys = this._sizeQuantities || {};
      // Clear old size properties first
      if (this.form) {
        this.form.querySelectorAll('[name^="properties[Size "]').forEach(el => el.remove());
      }
      Object.entries(qtys).forEach(([size, qty]) => {
        if (qty > 0) this._setProperty(`Size ${size}`, String(qty));
      });
    }

    /* ── Multi-variant cart intercept ───────────────────────────────────────── */

    _interceptProductForm() {
      if (this._cartIntercepted) return;
      this._cartIntercepted = true;
      if (!this.form) return;

      this._boundCartSubmit = this._onCartSubmit.bind(this);
      // Capture phase runs before product-form custom element's submit handler
      this.form.addEventListener('submit', this._boundCartSubmit, true);
    }

    async _onCartSubmit(e) {
      const qtys  = this._sizeQuantities || {};
      const total = Object.values(qtys).reduce((s, q) => s + q, 0);

      // Only intercept when there are sizing quantities; otherwise let standard submit through
      if (total === 0) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const items = this._buildCartItems();
      if (!items.length) return;

      // Find and disable the Add to Cart button while loading
      const addBtn = this.form.querySelector('[name="add"], [type="submit"]');
      if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Adding…'; }

      try {
        await this._addMultipleToCart(items);
        if (addBtn) { addBtn.disabled = false; addBtn.textContent = 'Add to cart'; }
        this._onCartAddSuccess();
      } catch (err) {
        console.error('[custom-selectors] Cart add failed:', err);
        if (addBtn) { addBtn.disabled = false; addBtn.textContent = 'Add to cart'; }
        alert('Could not add to cart: ' + (err.message || 'Please try again.'));
      }
    }

    _buildCartItems() {
      const sizingStep = this._sizingStep || this.el.querySelector('[data-step="sizing"]');
      let variantArray = [];
      try {
        variantArray = JSON.parse(sizingStep?.dataset.variantMap || '[]');
      } catch (_) { /* invalid JSON */ }

      const sizeOptIdx  = parseInt(sizingStep?.dataset.sizeOpt  || '2', 10) - 1; // 0-based
      const colorOptIdx = parseInt(sizingStep?.dataset.colorOpt || '1', 10) - 1;
      const optKeys     = ['o1', 'o2', 'o3'];
      const sizeKey     = optKeys[sizeOptIdx]  || 'o2';
      const colorKey    = optKeys[colorOptIdx] || 'o1';

      // Determine selected color from the current form variant
      const selectedVariantId = this.form?.querySelector('[name="id"]')?.value;
      const currentVariant    = variantArray.find(v => String(v.id) === String(selectedVariantId));
      const currentColor      = currentVariant?.[colorKey];

      const formProps = this._collectFormProperties();

      const items = [];
      Object.entries(this._sizeQuantities || {}).forEach(([size, qty]) => {
        if (qty <= 0) return;

        // Find matching variant: same color + this size
        let variant = variantArray.find(v =>
          v[sizeKey] === size &&
          (currentColor === undefined || v[colorKey] === currentColor) &&
          v.avail !== false
        );

        // Fallback: same size any color, then just use currently selected variant
        if (!variant) variant = variantArray.find(v => v[sizeKey] === size);
        if (!variant) variant = currentVariant;
        if (!variant) return;

        items.push({
          id:         variant.id,
          quantity:   qty,
          properties: { ...formProps },
        });
      });

      return items;
    }

    _collectFormProperties() {
      const props = {};
      if (!this.form) return props;
      this.form.querySelectorAll('[name^="properties["]').forEach((input) => {
        const match = input.name.match(/^properties\[(.+)\]$/);
        if (match) props[match[1]] = input.value;
      });
      return props;
    }

    async _addMultipleToCart(items) {
      const resp = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ items }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.description || err.message || `HTTP ${resp.status}`);
      }

      return resp.json();
    }

    /**
     * After a successful cart add, fetch fresh section HTML separately
     * (avoids the single-item vs multi-item response format mismatch) and
     * inject it directly — bypassing renderContents which has an is-empty
     * class bug that hides items in the drawer.
     */
    async _onCartAddSuccess() {
      const cartDrawer = document.querySelector('cart-drawer');

      try {
        // Fetch fresh section HTML — guaranteed to reflect the newly added items
        const sectionIds = cartDrawer
          ? 'cart-drawer,cart-icon-bubble'
          : 'cart-notification-product,cart-notification-button,cart-icon-bubble';

        const sections = await fetch(`/?sections=${sectionIds}`).then(r => r.json());

        // ── Cart icon bubble (header count) ───────────────────────────────────
        const bubbleHtml = sections['cart-icon-bubble'];
        if (bubbleHtml) {
          const parser = new DOMParser();
          const newEl  = parser.parseFromString(bubbleHtml, 'text/html').getElementById('cart-icon-bubble');
          const cur    = document.getElementById('cart-icon-bubble');
          if (newEl && cur) cur.innerHTML = newEl.innerHTML;
        }

        // ── Cart drawer ───────────────────────────────────────────────────────
        if (cartDrawer) {
          const drawerHtml = sections['cart-drawer'];
          if (drawerHtml) {
            const parser    = new DOMParser();
            const newDrawer = parser.parseFromString(drawerHtml, 'text/html').getElementById('CartDrawer');
            const curDrawer = document.getElementById('CartDrawer');
            if (newDrawer && curDrawer) curDrawer.innerHTML = newDrawer.innerHTML;
          }

          // Remove is-empty from both the custom element AND its .drawer__inner
          // child — Dawn's CSS hides items when either carries this class.
          cartDrawer.classList.remove('is-empty');
          cartDrawer.querySelector('.drawer__inner')?.classList.remove('is-empty');

          try { cartDrawer.open(); } catch (_) {}
        } else {
          window.location = '/cart';
        }

      } catch (err) {
        console.error('[custom-selectors] Cart refresh failed:', err);
        // Fallback: full reload shows the correct cart state
        window.location.reload();
      }
    }

    /* ── Artwork Editor ─────────────────────────────────────────────────────── */

    /**
     * Open the Konva mockup editor for a placement card.
     * Shows the selected-colour product image with the uploaded artwork on top;
     * only position / rotate / zoom controls are exposed (no hat-specific UI).
     */
    async _openArtworkEditor(zone, card) {
      const previewImg = zone.querySelector('.upload-zone__preview');
      if (!previewImg?.src || previewImg.hidden) return;

      // Always use the original uploaded artwork from IDB (previewUrl), NOT
      // previewImg.src — after the first "Save Mockup" that gets replaced with the
      // composited mockup, which would appear as a tiny t-shirt on the t-shirt.
      // Also read artworkState so we can restore position/scale/rotation on re-open.
      let artworkSrc   = previewImg.src; // safe fallback (first open, before any mockup save)
      let artworkState = null;
      if (this._designToken) {
        try {
          const placement = zone.dataset.uploadZone || 'front';
          const designs   = await this._idbGet('pcd_designs_' + this._designToken);
          if (designs?.[placement]?.previewUrl) artworkSrc   = designs[placement].previewUrl;
          if (designs?.[placement]?.artworkState) artworkState = designs[placement].artworkState;
        } catch (_) { /* keep fallbacks */ }
      }

      const placementName = card.dataset.placementName || 'front';
      const productImgSrc = this._getProductImageUrl();

      // Build full-screen overlay
      const overlay    = this._buildEditorOverlay(placementName);
      const canvasWrap = overlay.querySelector('[data-editor-canvas]');
      document.body.appendChild(overlay);
      document.body.style.overflow = 'hidden';

      // Load Konva.js if not already present on this page
      if (!window.Konva) {
        canvasWrap.innerHTML = '<div class="cs-editor-loading">Loading editor…</div>';
        try {
          await this._loadScript('https://unpkg.com/konva@9.3.16/konva.min.js');
        } catch (_) {
          canvasWrap.innerHTML = '<div class="cs-editor-loading cs-editor-loading--error">Failed to load editor. Please check your connection and refresh.</div>';
          return;
        }
        canvasWrap.innerHTML = '';
      }

      // Double-rAF so the overlay has a real layout and clientWidth/Height are non-zero
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const ctrl = this._initKonvaEditor(canvasWrap, productImgSrc, artworkSrc, placementName, artworkState);

          overlay.addEventListener('click', (ev) => {
            const btn = ev.target.closest('[data-editor-action]');
            if (!btn) return;

            const action  = btn.dataset.editorAction;
            const STEP    = 8;   // px per arrow press
            const ZOOM    = 0.1; // scale fraction per zoom press
            const ROT     = 15;  // degrees per rotate press
            const node    = ctrl.artworkNode();

            if (action === 'close') { this._closeEditor(overlay); return; }
            if (action === 'save')  {
              this._saveMockup(ctrl.stage, zone, card).then(() => this._closeEditor(overlay));
              return;
            }
            if (!node) return;

            switch (action) {
              case 'move-up':    node.y(node.y() - STEP); break;
              case 'move-down':  node.y(node.y() + STEP); break;
              case 'move-left':  node.x(node.x() - STEP); break;
              case 'move-right': node.x(node.x() + STEP); break;
              case 'move-center': ctrl.resetPos(); return;
              case 'rotate-left':  node.rotation((node.rotation() - ROT + 360) % 360); break;
              case 'rotate-right': node.rotation((node.rotation() + ROT) % 360); break;
              case 'zoom-out': {
                const s = Math.max(0.05, node.scaleX() - ZOOM);
                node.scale({ x: s, y: s });
                break;
              }
              case 'zoom-in': {
                const s = node.scaleX() + ZOOM;
                node.scale({ x: s, y: s });
                break;
              }
            }
            node.getLayer()?.batchDraw();
          });
        });
      });
    }

    /** Build the full-screen modal DOM — no Konva, just markup. */
    _buildEditorOverlay(placementName) {
      const ov = document.createElement('div');
      ov.className = 'cs-editor-overlay';
      ov.innerHTML = `
        <div class="cs-editor-modal">
          <div class="cs-editor-modal__header">
            <h3 class="cs-editor-modal__title">Preview — ${placementName}</h3>
            <button type="button" class="cs-editor-icon-btn" data-editor-action="close" aria-label="Close editor">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="cs-editor-canvas-wrap" data-editor-canvas></div>
          <div class="cs-editor-controls">
            <div class="cs-editor-tools">
              <button type="button" class="cs-editor-icon-btn" data-editor-action="rotate-left" title="Rotate left">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.45"/></svg>
              </button>
              <button type="button" class="cs-editor-icon-btn" data-editor-action="rotate-right" title="Rotate right">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-.49-3.45"/></svg>
              </button>
              <button type="button" class="cs-editor-icon-btn" data-editor-action="zoom-out" title="Shrink artwork">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
              </button>
              <button type="button" class="cs-editor-icon-btn" data-editor-action="zoom-in" title="Enlarge artwork">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
              </button>
              <div class="cs-editor-arrow-grid">
                <span></span>
                <button type="button" class="cs-editor-icon-btn" data-editor-action="move-up" aria-label="Move artwork up">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 5l8 9H4z"/></svg>
                </button>
                <span></span>
                <button type="button" class="cs-editor-icon-btn" data-editor-action="move-left" aria-label="Move artwork left">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M5 12l9-8v16z"/></svg>
                </button>
                <button type="button" class="cs-editor-icon-btn cs-editor-icon-btn--muted" data-editor-action="move-center" title="Reset position">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="3"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/></svg>
                </button>
                <button type="button" class="cs-editor-icon-btn" data-editor-action="move-right" aria-label="Move artwork right">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 12l-9 8V4z"/></svg>
                </button>
                <span></span>
                <button type="button" class="cs-editor-icon-btn" data-editor-action="move-down" aria-label="Move artwork down">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 19l-8-9h16z"/></svg>
                </button>
                <span></span>
              </div>
            </div>
            <button type="button" class="cs-editor-save-btn" data-editor-action="save">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
              Save Mockup
            </button>
          </div>
          <p class="cs-editor-hint">Drag artwork to reposition · Arrows for fine control · Rotate &amp; zoom to adjust size</p>
        </div>
      `;
      return ov;
    }

    /**
     * Create the Konva stage: light-grey canvas → white rounded-corner card
     * containing the product image → artwork draggable on top.
     * Returns a controller object for the caller to move / zoom the artwork node.
     */
    _initKonvaEditor(containerEl, productImgSrc, artworkSrc, placementName, savedState = null) {
      const K = window.Konva;

      // Cap at 760 so the CSS max-width boundary is respected even if
      // clientWidth reports the full viewport (flex measurement timing issue).
      const W = Math.min(containerEl.clientWidth || window.innerWidth, 760);

      // Derive H from the viewport, subtracting the modal chrome heights.
      const modal  = containerEl.closest('.cs-editor-modal');
      const taken  = ['.cs-editor-modal__header', '.cs-editor-controls', '.cs-editor-hint']
        .reduce((acc, sel) => acc + (modal?.querySelector(sel)?.offsetHeight || 0), 0);
      const H = Math.max(window.innerHeight - taken, 280);
      // Pin the canvas-wrap height so the stage fills it correctly.
      containerEl.style.width  = W + 'px';
      containerEl.style.height = H + 'px';

      // Card geometry — product image lives in a padded rounded-rect.
      const PAD = 16;
      const COR = 16;
      const cX  = PAD;
      const cY  = PAD;
      const cW  = W - PAD * 2;
      const cH  = H - PAD * 2;

      // Artwork defaults: xFrac/yFrac = centre as fraction of card; maxFrac = max width fraction.
      // xFrac is from the viewer's left. The t-shirt faces the viewer, so the
      // wearer's Left Chest is on the viewer's RIGHT (xFrac > 0.5), and vice versa.
      const PLACEMENT_DEFAULTS = {
        'Left Chest':   { xFrac: 0.62, yFrac: 0.33, maxFrac: 0.14 },
        'Right Chest':  { xFrac: 0.36, yFrac: 0.33, maxFrac: 0.14 },
        'Full Back':    { xFrac: 0.50, yFrac: 0.45, maxFrac: 0.40 },
        'Left Sleeve':  { xFrac: 0.86, yFrac: 0.42, maxFrac: 0.12 },
        'Right Sleeve': { xFrac: 0.14, yFrac: 0.42, maxFrac: 0.12 },
        'Full Front':   { xFrac: 0.50, yFrac: 0.45, maxFrac: 0.38 },
      };
      const def = PLACEMENT_DEFAULTS[placementName] || { xFrac: 0.50, yFrac: 0.40, maxFrac: 0.22 };

      const stage    = new K.Stage({ container: containerEl, width: W, height: H });
      const bgLayer  = new K.Layer();
      const artLayer = new K.Layer();
      stage.add(bgLayer);
      stage.add(artLayer);
      // Stash card geometry on the stage so _saveMockup can normalise artwork coords.
      stage._pcdLayout = { cX, cY, cW, cH };

      const ctrl = {
        stage,
        _artNode: null,
        _defX: 0, _defY: 0, _defScale: 1,
        artworkNode() { return this._artNode; },
        resetPos() {
          if (!this._artNode) return;
          this._artNode.position({ x: this._defX, y: this._defY });
          this._artNode.scale({ x: this._defScale, y: this._defScale });
          this._artNode.rotation(0);
          this._artNode.getLayer()?.batchDraw();
        },
      };

      // ── 1. Grey canvas background ────────────────────────────────────────
      bgLayer.add(new K.Rect({ x: 0, y: 0, width: W, height: H, fill: '#e4e4e4', listening: false }));

      // ── 2. White card with shadow ────────────────────────────────────────
      bgLayer.add(new K.Rect({
        x: cX, y: cY, width: cW, height: cH,
        cornerRadius: COR,
        fill: '#ffffff',
        shadowColor: 'rgba(0,0,0,0.22)',
        shadowBlur: 14,
        shadowOffset: { x: 0, y: 4 },
        listening: false,
      }));

      // ── 3. Product image — fit-scaled, centred inside the card ──────────
      if (productImgSrc) {
        const bgImg = new Image();
        bgImg.crossOrigin = 'anonymous';
        bgImg.onload = () => {
          // Fit: never overflow the card edges, centre on both axes.
          const scale = Math.min(cW / bgImg.naturalWidth, cH / bgImg.naturalHeight);
          const sw    = Math.round(bgImg.naturalWidth  * scale);
          const sh    = Math.round(bgImg.naturalHeight * scale);
          const ix    = cX + Math.round((cW - sw) / 2);
          const iy    = cY + Math.round((cH - sh) / 2);
          bgLayer.add(new K.Image({ x: ix, y: iy, width: sw, height: sh, image: bgImg, listening: false }));
          bgLayer.batchDraw();
        };
        bgImg.onerror = () => bgLayer.batchDraw(); // white card already visible
        bgImg.src = productImgSrc;
      }

      // ── 5. Artwork — restored from saved state, or placed at default position ─
      const artImg = new Image();
      artImg.crossOrigin = 'anonymous';
      artImg.onload = () => {
        let ox, oy, scale;

        if (savedState) {
          // Restore saved position. Coords are stored as fractions of cW/cH so they
          // survive viewport size changes between sessions.
          const renderW = savedState.wNorm * cW;
          scale = renderW / artImg.naturalWidth;
          ox    = cX + savedState.xNorm * cW;
          oy    = cY + savedState.yNorm * cH;
        } else {
          // First open — use placement defaults.
          const targetX = cX + def.xFrac * cW;
          const targetY = cY + def.yFrac * cH;
          const maxPx   = cW * def.maxFrac;
          scale         = Math.min(maxPx / artImg.naturalWidth, maxPx / artImg.naturalHeight);
          const sw      = artImg.naturalWidth  * scale;
          const sh      = artImg.naturalHeight * scale;
          ox            = targetX - sw / 2;
          oy            = targetY - sh / 2;
        }

        ctrl._defX     = ox;
        ctrl._defY     = oy;
        ctrl._defScale = scale;

        const node = new K.Image({
          x: ox, y: oy,
          width:  artImg.naturalWidth,
          height: artImg.naturalHeight,
          image: artImg,
          scaleX: scale,
          scaleY: scale,
          rotation: savedState?.rotation ?? 0,
          draggable: true,
        });

        ctrl._artNode = node;
        artLayer.add(node);
        artLayer.batchDraw();
      };
      artImg.onerror = () => {
        console.warn('[custom-selectors] Editor: artwork load failed');
      };
      artImg.src = artworkSrc;

      return ctrl;
    }

    /**
     * Replace the main product gallery image with the mockup data URL.
     * Clears srcset/sizes so the browser doesn't override our swap, and marks
     * the element so it can be identified later (e.g. to revert on variant change).
     */
    _updateProductPageImage(dataUrl) {
      const selectors = [
        '.product__media-item--active .product__media img',
        '.product__media-item.is-active .product__media img',
        '.product__media-item--active img',
        '.product__media img',
        '.product-single__photo img',
        '.product__main-photos img',
        '[data-product-media-type="image"] img',
      ];

      for (const sel of selectors) {
        const img = document.querySelector(sel);
        if (!img) continue;

        // Preserve the original src/srcset the first time so we can revert if needed
        if (!img.dataset.pcdOriginalSrc) {
          img.dataset.pcdOriginalSrc   = img.currentSrc || img.src || '';
          img.dataset.pcdOriginalSrcset = img.srcset || '';
        }

        // Swap in the mockup — clear srcset/sizes so the browser uses our src
        img.srcset = '';
        img.sizes  = '';
        img.src    = dataUrl;

        // Also clear any <source> elements inside a parent <picture>
        const picture = img.closest('picture');
        if (picture) {
          picture.querySelectorAll('source').forEach(s => {
            if (!s.dataset.pcdOriginalSrcset) {
              s.dataset.pcdOriginalSrcset = s.srcset || '';
            }
            s.srcset = '';
          });
        }

        console.log('[custom-selectors] Product image updated with mockup');
        return; // only update the first/active image
      }
    }

    /**
     * Try several DOM selectors to find the currently displayed product image URL.
     * Works with Dawn's media gallery and various slider implementations.
     */
    _getProductImageUrl() {
      const selectors = [
        '.product__media-item--active .product__media img',
        '.product__media-item.is-active .product__media img',
        '.product__media-item--active img',
        '.product__media img',
        '.product-single__photo img',
        '.product__main-photos img',
        '[data-product-media-type="image"] img',
      ];

      for (const sel of selectors) {
        const img = document.querySelector(sel);
        if (!img) continue;
        // Prefer the stored original URL if this element was swapped with a mockup
        const src = img.dataset.pcdOriginalSrc || img.currentSrc || img.src || '';
        if (src && src.startsWith('http') && !src.includes('no-image') && !src.startsWith('data:')) {
          // Strip any existing size/version parameters and request 800px width
          return src.replace(/\?.*$/, '') + '?width=800';
        }
      }
      return null;
    }

    /**
     * Export the Konva stage as a PNG, update the upload zone preview,
     * and persist the mockupUrl alongside the original artwork in IDB.
     */
    async _saveMockup(stage, zone, card) {
      let dataUrl;
      try {
        dataUrl = stage.toDataURL({ mimeType: 'image/png', quality: 0.9, pixelRatio: 1.5 });
      } catch (corsErr) {
        // Canvas is tainted by a cross-origin product image — cannot export
        console.warn('[custom-selectors] Canvas tainted, mockup not saved:', corsErr);
        return;
      }

      // Update the upload-zone preview to show the mockup instead of the raw artwork
      const doneState  = zone.querySelector('.upload-zone__state--done');
      const previewImg = doneState?.querySelector('.upload-zone__preview');
      if (previewImg) {
        previewImg.src    = dataUrl;
        previewImg.hidden = false;
      }
      const fileIcon = doneState?.querySelector('.upload-zone__file-icon');
      if (fileIcon) fileIcon.hidden = true;

      // Replace the main product gallery image with the mockup
      this._updateProductPageImage(dataUrl);

      // Save mockupUrl + artwork state to IDB.
      // artworkState stores position/scale as fractions of the card so it restores
      // correctly even if the viewport size changes between sessions.
      if (this._designToken) {
        try {
          const placement = zone.dataset.uploadZone || 'front';
          const key       = 'pcd_designs_' + this._designToken;
          const existing  = (await this._idbGet(key)) || {};

          let artworkState = null;
          const artLayer = stage.getLayers()[1];
          const artNode  = artLayer?.getChildren()[0];
          const layout   = stage._pcdLayout;
          if (artNode && layout) {
            const { cX, cY, cW, cH } = layout;
            artworkState = {
              xNorm:    (artNode.x() - cX) / cW,
              yNorm:    (artNode.y() - cY) / cH,
              wNorm:    (artNode.width() * artNode.scaleX()) / cW,
              rotation: artNode.rotation(),
            };
          }

          existing[placement] = { ...(existing[placement] || {}), mockupUrl: dataUrl, artworkState };
          await this._idbPut(key, existing);
        } catch (idbErr) {
          console.warn('[custom-selectors] IDB mockup save failed:', idbErr);
        }
      }
    }

    /** Tear down the Konva stage and remove the overlay. */
    _closeEditor(overlay) {
      const canvasWrap = overlay.querySelector('[data-editor-canvas]');
      if (canvasWrap && window.Konva) {
        // Find and destroy the stage that owns this container to free GPU memory
        try {
          const s = window.Konva.stages.find((st) => st.container() === canvasWrap);
          if (s) s.destroy();
        } catch (_) { /* silent */ }
      }
      overlay.remove();
      document.body.style.overflow = '';
    }

    /** Dynamically load an external script (idempotent). */
    _loadScript(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
        const s = document.createElement('script');
        s.src     = src;
        s.onload  = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }

  }

  /* ── Init ──────────────────────────────────────────────────────────────────── */
  function init() {
    document.querySelectorAll('.custom-selectors').forEach((el) => {
      if (!el.dataset.csInit) {
        el.dataset.csInit = '1';
        new CustomSelectors(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
